// Tasarim Motoru — URUN BASKI SABLONLARI ve tasarim belgesi tipleri.
//
// Bir sablon = urunun baski dosyasi geometrisi: olcu (inc), DPI, tasma payi
// (bleed), guvenli bolge ve varsa delik. Tuval = kesim olcusu + iki yanda tasma.
// Editor kesim cizgisini (duz), guvenli bolgeyi (kesikli) ve deligi tuvalin
// ustunde gosterir; bunlar disa aktarilan PNG'ye GIRMEZ.
//
// Varyant: ayni urunun sekil secenekleri (ornament: daire / kalp). Her varyant
// kendi katmanlarini tutar; ilk geciste digerinden kopyalanir.

export type PrintShape = "rect" | "ellipse" | "heart";

/** Baski teknigi — denetim kurallari buna gore degisir (Rexven uretimi, 2026-10):
 *  sublimation: beyaz murekkep yok (beyaz = urunun rengi), yari saydamlik soluk cikar
 *  dtg: dogrudan kumasa murekkep; koyu kumasta yari saydam kenar hale birakir
 *  dtf: filme basilip sicak presle kumasa aktarilir (Rexven tisortleri); arkasi
 *       yapiskan tozlu, kil inceligindeki cizgi tutunamaz soyulur. Rulo 22 inc,
 *       standart baski eni en fazla 11 inc (yan yana iki tane sigsin diye)
 *  uv: UV flat dogrudan baski (paspas, yard sign, coin); opak beyaz basabilir
 *  uvdtf: UV DTF transfer, sert zemine yapistirilan kabartmali baski (kupa, cam
 *         bardak, taki tabagi); beyaz basabilir, cok ince detay sokerken kopabilir */
export type PrintTechnique = "sublimation" | "dtg" | "dtf" | "uv" | "uvdtf";

export interface PrintVariant {
  id: string;
  label: string;
  shape: PrintShape;
  /** Kesim (trim) olcusu, inc */
  widthIn: number;
  heightIn: number;
  /** Delik: merkez ve yaricap KESIM kutusuna oranla (0..1, yaricap genislige oranla) */
  hole?: { cx: number; cy: number; r: number };
  /** Boy siniri bilinmiyor: heightIn yalnizca editor tuvali icin, "cok uzun"
   *  denetimi yapilmaz */
  heightOpen?: boolean;
}

export interface PrintTemplate {
  key: string;
  label: string;
  /** Urun tipi anahtari (ProductTypeProfile.key) */
  productTypeKey: string;
  dpi: number;
  /** Kesimden disari tasma payi, inc */
  bleedIn: number;
  /** Kesimden iceri guvenli bolge payi, inc */
  safeIn: number;
  variants: PrintVariant[];
  hint?: string;
  technique: PrintTechnique;
  /** Sarma baski: dolu arka plan normaldir (kupa/termos) */
  wrap?: boolean;
}

export const PRINT_TEMPLATES: PrintTemplate[] = [
  {
    key: "ornament", label: "Seramik ornament", productTypeKey: "ornament", technique: "sublimation", dpi: 300, bleedIn: 0.0625, safeIn: 0.125,
    hint: "3 inc seramik sus; ust ortada asma deligi",
    variants: [
      { id: "circle", label: "Daire", shape: "ellipse", widthIn: 3, heightIn: 3, hole: { cx: 0.5, cy: 0.085, r: 0.035 } },
      { id: "heart", label: "Kalp", shape: "heart", widthIn: 3, heightIn: 2.9, hole: { cx: 0.5, cy: 0.2, r: 0.035 } },
    ],
  },
  {
    key: "mug11", label: "Kupa 11oz", productTypeKey: "mug", technique: "uvdtf", wrap: true, dpi: 300, bleedIn: 0.1, safeIn: 0.25,
    hint: "UV DTF; iki yuze ya da tam sarma basilir, kulp tarafi kenarlarda kalir. 15oz da var (olcusu girilmedi)",
    variants: [{ id: "wrap", label: "Sarma", shape: "rect", widthIn: 8.5, heightIn: 3.5 }],
  },
  {
    key: "tumbler20", label: "Termos 20oz", productTypeKey: "tumbler", technique: "sublimation", wrap: true, dpi: 300, bleedIn: 0.1, safeIn: 0.25,
    variants: [{ id: "wrap", label: "Sarma", shape: "rect", widthIn: 9.3, heightIn: 8.2 }],
  },
  {
    key: "doormat", label: "Paspas (Coco)", productTypeKey: "doormat", technique: "uv", dpi: 150, bleedIn: 0.25, safeIn: 1,
    hint: "UV flat; hindistan cevizi lifi rengi soldurur. En cok satan 18x30 ve 24x36",
    // "rect" kimligi eski tasarim belgeleri icin korunur (24x16)
    variants: [
      { id: "rect", label: "24x16", shape: "rect", widthIn: 24, heightIn: 16 },
      { id: "30x18", label: "30x18", shape: "rect", widthIn: 30, heightIn: 18 },
      { id: "36x24", label: "36x24", shape: "rect", widthIn: 36, heightIn: 24 },
      { id: "48x24", label: "48x24", shape: "rect", widthIn: 48, heightIn: 24 },
      { id: "60x36", label: "60x36", shape: "rect", widthIn: 60, heightIn: 36 },
    ],
  },
  {
    key: "trinket", label: "Taki tabagi", productTypeKey: "trinket-dish", technique: "uvdtf", dpi: 300, bleedIn: 0.0625, safeIn: 0.2,
    hint: "UV DTF; yalnizca ortadaki cukura basilir, kenarlara baski yok",
    variants: [
      { id: "square", label: "Kare", shape: "rect", widthIn: 4, heightIn: 4 },
      { id: "heart", label: "Kalp", shape: "heart", widthIn: 4, heightIn: 3.9 },
      { id: "round", label: "Yuvarlak", shape: "ellipse", widthIn: 4, heightIn: 4 },
    ],
  },
  {
    key: "coin", label: "Hatira para", productTypeKey: "coin", technique: "uv", dpi: 300, bleedIn: 0.03, safeIn: 0.1,
    hint: "UV print, kapsul + keseyle; lazer kazima secenegi geliyor (gumuste siyah cikar)",
    variants: [{ id: "round", label: "Yuvarlak", shape: "ellipse", widthIn: 1.6, heightIn: 1.6 }],
  },
  {
    key: "tshirt", label: "Tisort on baski", productTypeKey: "tshirt", technique: "dtf", dpi: 300, bleedIn: 0, safeIn: 0,
    hint: "DTF + sicak pres. Baski eni tasarimin eninden olceklenir (en fazla 11 inc); tasarimi KIRP, bos kenar baskiyi kucultur",
    variants: [
      { id: "front", label: "On", shape: "rect", widthIn: 11, heightIn: 16 },
      // Big print: 16 inc en, 22 inc rulonun tamamini kaplar (+$2.2, 2026-10). Boyu
      // videoda verilmedi — tuval icin 20, denetimde boy siniri yok
      { id: "big", label: "Big print (16 inc)", shape: "rect", widthIn: 16, heightIn: 20, heightOpen: true },
    ],
  },
];

export function printTemplateByKey(key: string): PrintTemplate | undefined {
  return PRINT_TEMPLATES.find((t) => t.key === key);
}

/** Tuval geometrisi (piksel): tuval, kesim kutusu, guvenli kutu, delik */
export function printGeometry(t: PrintTemplate, v: PrintVariant) {
  const bleed = t.bleedIn * t.dpi, safe = t.safeIn * t.dpi;
  const trimW = v.widthIn * t.dpi, trimH = v.heightIn * t.dpi;
  const W = Math.round(trimW + bleed * 2), H = Math.round(trimH + bleed * 2);
  const trim = { x: bleed, y: bleed, w: trimW, h: trimH };
  const safeBox = { x: bleed + safe, y: bleed + safe, w: Math.max(1, trimW - safe * 2), h: Math.max(1, trimH - safe * 2) };
  const hole = v.hole ? { x: trim.x + v.hole.cx * trimW, y: trim.y + v.hole.cy * trimH, r: v.hole.r * trimW } : null;
  return { W, H, trim, safe: safeBox, hole };
}

// ---- Tasarim belgesi (Design.metadata.engine) ----

export interface EngineTextLayer {
  id: string;
  type: "text";
  text: string;
  /** Konum/boyut tuvale oranla (0..1) — varyantlar arasi tasinabilsin */
  x: number;
  y: number;
  w: number;
  rotation: number;
  fontFamily: string;
  /** Yazi boyu tuval yuksekligine oranla */
  fontSize: number;
  bold?: boolean;
  italic?: boolean;
  color: string;
  align: "left" | "center" | "right";
  letterSpacing: number;
  lineHeight: number;
  uppercase?: boolean;
  opacity?: number;
  /** Kisisellestirme alani anahtari (or. "names") — siparis gelince degisir */
  personalize?: string | null;
  /** Stil matrisinin yerlestirdigi rol (stil degisince metin korunur) */
  role?: "title" | "names" | "date";
}

export interface EngineImageLayer {
  id: string;
  type: "image";
  url: string;
  x: number;
  y: number;
  w: number;
  h: number;
  rotation: number;
  opacity?: number;
  personalize?: string | null;
  role?: "illus";
}

export type EngineLayer = EngineTextLayer | EngineImageLayer;

export interface EngineDoc {
  version: 1;
  templateKey: string;
  activeVariant: string;
  /** Arka plan rengi (null = saydam) */
  background: string | null;
  variants: Record<string, EngineLayer[]>;
  /** Stil matrisi secimi (Faz 2) — yeniden acinca ayni daldan devam */
  style?: import("./engine-styles").StyleBranch | null;
}
